import { Grid, MenuItem, TextField, FormControlLabel, Switch } from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { Maybe, UserRole, ClientPortalUserModel, UpsertPortalUserInput } from "../../gql-types.generated";
import AddEditDialog from "./AddEditDialog";
import { isEmailValid } from "../../util/Validation";

interface PortalUserDialogProps {
    isOpen: boolean;
    onClose: () => void;
    tenantId: string;
    user?: ClientPortalUserModel | null | undefined;
    error?: Error | undefined;
    onSave: (portalUserInput: UpsertPortalUserInput) => void;
}

const roleOptions = [
    {
        value: UserRole.Reader,
        label: 'Reader',
    },
    {
        value: UserRole.Admin,
        label: 'Admin',
    },
];

const PortalUserDialog: React.FC<PortalUserDialogProps> = props => {
    const { isOpen, tenantId, user, error, onClose, onSave } = props;
    const id = user?.id;
    const [isFormDirty, setIsFormDirty] = useState(false); // Dirty state of the form.
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [customerRole, setCustomerRole] = useState<UserRole>(UserRole.Reader);
    const [subscriptionRole, setSubscriptionRole] = useState<UserRole>(UserRole.Reader);
    const [emailInvalid, setEmailInvalid] = useState(false); // Default invalid state to false
    const [emailErrorText, setEmailErrorText] = useState(''); // Default invalid state to false
    const [submitted, setSubmitted] = useState(false); // Submitted state of the form

    useEffect(() => {
        // if have a user, then populate for Edit
        if (user) {
             
            if (user.firstName) {
                setFirstName(user.firstName);
            }
            if (user.lastName) {
                setLastName(user.lastName);
            }
            if (user.email) {
                setEmail(user.email);
            }
            if (user.customerRole) {
                setCustomerRole(user.customerRole);
            }
            if (user.subscriptionRole) {
                setSubscriptionRole(user.subscriptionRole);
            }
        }
        else {
            setToDefaults();
        }
    }, [user]);

    useEffect(() => {
        // set submitted to false and clear fields when dialog is closed
        if (!isOpen) {
            setToDefaults();
            setSubmitted(false);
            setIsFormDirty(false);
        }
    }, [isOpen]);

    const setToDefaults = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setCustomerRole(UserRole.Reader);
        setSubscriptionRole(UserRole.Reader);
    };

    const onError = () => {
        setSubmitted(false);
    }

    const validateAndSetEmail = (toValidate: string) => {
        if (toValidate || toValidate === "") {
            const isValid = isEmailValid(toValidate);
            setEmailInvalid(!isValid);
            if (isValid) {
                setIsFormDirty(true);
                setEmailErrorText('');
            } else {
                setEmailErrorText('Invalid format.  Enter a valid email address');
            }
            setEmail(toValidate);
        }
    }

    const isFormValid = () => {
        let requiredCheck = firstName?.length > 0 && lastName?.length > 0 && email?.length > 0 && customerRole !== null;
        return isFormDirty && requiredCheck && !emailInvalid;
    };

    const submitForm = () => {
        setSubmitted(true);
        onSave({
            firstName, 
            lastName, 
            email, 
            customerRole,
            subscriptionRole,
            tenantId, 
            id
        });
    };

    const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setFirstName(event.target.value);
    };
    const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setLastName(event.target.value);
    };
    const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        validateAndSetEmail(event.target.value);
    };

    const handleCustomerRoleChange = (event: ChangeEvent<HTMLInputElement>) => {
        // Set as the Role value structure.
        setIsFormDirty(true);
        const roleChangeValue = event.target.value || UserRole.Reader;
        setCustomerRole(roleChangeValue as UserRole);
    };

    const handleSubscriptionRoleChange = (event: ChangeEvent<HTMLInputElement>) => {
        // Set as the Role value structure.
        setIsFormDirty(true);
        const roleChangeValue = event.target.value || UserRole.Reader;
        setSubscriptionRole(roleChangeValue as UserRole);
    };

    
    const firstNameProps = {
        'aria-label': 'first name',
        'maxLength': 100,
    };
    const lastNameProps = {
        'aria-label': 'last name',
        'maxLength': 100,
    };
    const emailProps = {
        "aria-label": "email",
        maxLength: 100,
    };

    const isEmailDisabled = () => {
        /**disable the email if the form
        is submitted or its an update 
        operation(id is not null)**/
        if (submitted || id) {
            return true;
        }
        return false;
    };

    const getRoleOptions = () => {
        return roleOptions.map(roleValue => (
            <MenuItem key={roleValue.value} value={roleValue.value}>
                {roleValue.label}
            </MenuItem>
        ));
    }

    return (
        <AddEditDialog
            isOpen={isOpen}
            isSubmitted={submitted}
            id={id ?? undefined}
            titlePrefix={id ? "Edit" : "Add"}
            entityName="Portal User"
            onClose={onClose}
            onSave={submitForm}
            validate={isFormValid}
            onError={onError}
            error={error}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        itemID="dialog-portal-user-first-name"
                        fullWidth
                        disabled={submitted}
                        autoFocus
                        value={firstName}
                        label="First Name"
                        inputProps={firstNameProps}
                        onChange={handleFirstNameChange}
                        autoComplete="off"
                        required
                        data-cy="dialog-portal-user-first-name"
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        itemID="dialog-portal-user-last-name"
                        fullWidth
                        disabled={submitted}
                        value={lastName}
                        label="Last Name"
                        inputProps={lastNameProps}
                        onChange={handleLastNameChange}
                        autoComplete="off"
                        required
                        data-cy="dialog-portal-user-last-name"
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        itemID="dialog-portal-user-email"
                        fullWidth
                        error={emailInvalid}
                        helperText={emailErrorText}
                        disabled={isEmailDisabled()}
                        value={email}
                        label="Email"
                        inputProps={emailProps}
                        onChange={handleEmailChange}
                        autoComplete="off"
                        required
                        data-cy="dialog-portal-user-email"
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        itemID="dialog-portal-user-customer-role"
                        fullWidth
                        onChange={handleCustomerRoleChange}
                        defaultValue={UserRole.Reader}
                        disabled={submitted}
                        inputProps={{ 'aria-label': 'role' }}
                        label="Customer Role"
                        value={customerRole}
                        data-cy="dialog-portal-user-customer-role"
                        variant="standard"
                        select
                    >
                        {getRoleOptions()}
                    </TextField>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        itemID="dialog-portal-user-sub-role"
                        fullWidth
                        onChange={handleSubscriptionRoleChange}
                        defaultValue={UserRole.Reader}
                        disabled={submitted}
                        inputProps={{ 'aria-label': 'role' }}
                        label="Subscription Role"
                        value={subscriptionRole}
                        data-cy="dialog-portal-user-sub-role"
                        variant="standard"
                        select
                    >
                        {getRoleOptions()}
                    </TextField>
                </Grid>
                
            </Grid>
        </AddEditDialog>
    );
};

export default PortalUserDialog;